import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check } from 'lucide-react';
import { useSafePostHog } from '../components/PostHogProvider';
import PublicNavbar from '../components/PublicNavbar';

const PricingPage = () => {
  const navigate = useNavigate();
  const posthog = useSafePostHog();

  const faqs = [
    {
      q: "Can I switch from monthly to yearly?",
      a: "Yes! You can upgrade to yearly billing anytime from your subscription settings. You'll receive a prorated credit for your remaining monthly subscription."
    },
    {
      q: "What happens if I cancel?",
      a: "You'll continue to have Premium access until the end of your current billing period, then you'll be automatically downgraded to the Free plan."
    },
    {
      q: "Is there a trial period?",
      a: "Yes! We offer a 7-day money-back guarantee for first-time Premium subscribers. Try it risk-free!"
    },
    {
      q: "Can I upgrade my Free plan anytime?",
      a: "Absolutely! Upgrade to Premium anytime from your dashboard or subscription page."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFDF7] to-white">
      <PublicNavbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-28 pb-10 sm:pt-36 sm:pb-16 overflow-hidden">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-[#1B4332] mb-4 sm:mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
            CHOOSE YOUR PLAN
          </h2>
          <p className="text-[15px] sm:text-xl text-[#1B4332]/70 px-2 mb-4">Simple, transparent pricing for every cat parent</p>
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs sm:text-sm font-bold px-4 py-2 rounded-full">
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
            Join 500+ Early Adopters. These early-bird prices are valid for the next 48 hours only.
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 sm:gap-8 mb-12 sm:mb-20">
          <div className="bg-white border-2 border-[#1B4332]/10 shadow-[0_8px_30px_rgb(27,67,50,0.08)] rounded-3xl p-5 sm:p-8 hover:-translate-y-2 transition-all" data-testid="free-plan-card">
            <p className="text-sm uppercase tracking-wide font-bold text-[#1B4332]/60 mb-2">Free</p>
            <p className="text-4xl sm:text-6xl font-black text-[#1B4332] mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>$0</p>
            <p className="text-[#1B4332]/60 mb-6">Forever free</p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-[#1B4332]/80">3 ingredient scans/month</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-[#1B4332]/80">5 behavior questions/month</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-[#1B4332]/80">Basic analytics</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-[#1B4332]/80">Cat profiles</span>
              </li>
            </ul>
            <Button variant="outline" onClick={() => { posthog.capture('subscription_clicked', { plan_name: 'free', source: 'pricing_page' }); navigate('/signup'); }} className="w-full min-h-[44px] rounded-full font-bold border-2 border-[#1B4332]">
              Get Started
            </Button>
          </div>

          <div className="bg-[#1B4332] text-white shadow-[0_20px_50px_rgba(191,219,56,0.15)] rounded-3xl p-5 sm:p-8 hover:-translate-y-2 transition-all relative ring-4 ring-[#BFDB38]/30 z-10 scale-100 sm:scale-[1.03]" data-testid="monthly-plan-card">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#BFDB38] text-[#1B4332] px-6 py-2 rounded-full text-sm font-black uppercase whitespace-nowrap">
              MOST POPULAR
            </div>
            <p className="text-sm uppercase tracking-wide font-bold text-white/80 mb-2">Premium Monthly</p>
            <div className="flex items-baseline gap-2 mb-1">
               <span className="text-lg text-white/30 line-through font-bold">$12.99</span>
               <p className="text-4xl sm:text-6xl font-black mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>$8.99</p>
            </div>
            <p className="text-white/70 mb-6">Per month</p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-white"><strong>Complete Peace of Mind</strong> (Unlimited Scans)</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-white">Unlimited behavior chat</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-white">Early Access to AI Health Predictor</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-white">Detailed Health Trend Reports</span>
              </li>
            </ul>
            <Button onClick={() => { posthog.capture('subscription_clicked', { plan_name: 'premium_monthly', price: '$8.99', source: 'pricing_page' }); navigate('/subscription'); }} className="w-full min-h-[44px] rounded-full font-bold bg-[#BFDB38] text-[#1B4332] hover:bg-[#a8c232]">
              Start Monthly
            </Button>
          </div>

          <div className="bg-white border-2 border-[#1B4332]/10 shadow-[0_8px_30px_rgb(27,67,50,0.08)] rounded-3xl p-5 sm:p-8 hover:-translate-y-2 transition-all relative" data-testid="yearly-plan-card">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm uppercase tracking-wide font-bold text-[#1B4332]/60">Premium Yearly</p>
              <span className="text-[10px] font-black text-[#BFDB38] uppercase tracking-wider">BEST VALUE - SAVE 25%</span>
            </div>
            <div className="flex items-baseline gap-2 mb-1">
               <span className="text-lg text-gray-400 line-through font-bold">$119.99</span>
               <p className="text-4xl sm:text-6xl font-black text-[#1B4332]" style={{ fontFamily: 'Nunito, sans-serif' }}>$79.99</p>
            </div>
            <p className="text-[#1B4332]/60 mb-6">Billed annually</p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-[#1B4332]/80"><strong>Complete Peace of Mind</strong> (Unlimited Scans)</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-[#1B4332]/80">Unlimited behavior chat</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-[#1B4332]/80">Early Access to AI Health Predictor</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-[#1B4332]/80">Detailed Health Trend Reports</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#BFDB38] flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span className="text-[#1B4332]/80">Priority access to new AI Models</span>
              </li>
            </ul>
            <Button onClick={() => { posthog.capture('subscription_clicked', { plan_name: 'premium_yearly', price: '$79.99', source: 'pricing_page' }); navigate('/subscription'); }} className="w-full min-h-[44px] rounded-full font-bold bg-[#1B4332] text-white hover:bg-[#1B4332]/90">
              Secure My Early Bird Discount
            </Button>
          </div>


        </div>

        <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-12 border-2 border-[#1B4332]/10 mb-12">
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#1B4332] mb-6 sm:mb-8 text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>
            FEATURE COMPARISON
          </h3>
          <div className="overflow-x-auto pb-4">
            <table className="w-full text-sm sm:text-base">
              <thead>
                <tr className="border-b-2 border-[#1B4332]/10">
                  <th className="text-left py-4 px-4 text-[#1B4332] font-bold">Feature</th>
                  <th className="text-center py-4 px-4 text-[#1B4332] font-bold">Free</th>
                  <th className="text-center py-4 px-4 text-[#1B4332] font-bold">Premium</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#1B4332]/10">
                  <td className="py-4 px-4 text-[#1B4332]/80">Ingredient scans</td>
                  <td className="text-center py-4 px-4 text-[#1B4332]/80">3/month</td>
                  <td className="text-center py-4 px-4 text-[#BFDB38] font-bold">Unlimited</td>
                </tr>
                <tr className="border-b border-[#1B4332]/10">
                  <td className="py-4 px-4 text-[#1B4332]/80">Behavior questions</td>
                  <td className="text-center py-4 px-4 text-[#1B4332]/80">5/month</td>
                  <td className="text-center py-4 px-4 text-[#BFDB38] font-bold">Unlimited</td>
                </tr>
                <tr className="border-b border-[#1B4332]/10">
                  <td className="py-4 px-4 text-[#1B4332]/80">Cat profiles</td>
                  <td className="text-center py-4 px-4"><Check className="w-5 h-5 text-[#BFDB38] mx-auto" strokeWidth={3} /></td>
                  <td className="text-center py-4 px-4"><Check className="w-5 h-5 text-[#BFDB38] mx-auto" strokeWidth={3} /></td>
                </tr>
                <tr className="border-b border-[#1B4332]/10">
                  <td className="py-4 px-4 text-[#1B4332]/80">Analytics dashboard</td>
                  <td className="text-center py-4 px-4 text-[#1B4332]/80">Basic</td>
                  <td className="text-center py-4 px-4 text-[#BFDB38] font-bold">Advanced</td>
                </tr>
                <tr className="border-b border-[#1B4332]/10">
                  <td className="py-4 px-4 text-[#1B4332]/80">Priority support</td>
                  <td className="text-center py-4 px-4 text-[#1B4332]/80">-</td>
                  <td className="text-center py-4 px-4"><Check className="w-5 h-5 text-[#BFDB38] mx-auto" strokeWidth={3} /></td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-[#1B4332]/80">Early feature access</td>
                  <td className="text-center py-4 px-4 text-[#1B4332]/80">-</td>
                  <td className="text-center py-4 px-4"><Check className="w-5 h-5 text-[#BFDB38] mx-auto" strokeWidth={3} /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#1B4332] mb-6 sm:mb-8 text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>
            FREQUENTLY ASKED QUESTIONS
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 border-2 border-[#1B4332]/10">
                <h4 className="text-lg font-bold text-[#1B4332] mb-2">{faq.q}</h4>
                <p className="text-[#1B4332]/80">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;